// Session-authenticated JSON client for the Inertia frontend's background XHR
// (timer saves, task CRUD, calendar). Talks to /web/internal/* using the Rails
// cookie session + CSRF token (from the <meta name="csrf-token"> tag), NOT the
// Bearer token the old Next.js app used. Api::V1 stays token-only for Tauri/OpenClaw.

import { t } from './i18n'
import { csrfToken } from './csrf'
import type { PageContext } from '../components/Agent/types'
import type { PianoBindingInput } from '../types/project'
import type { ToolSharingChoice } from '../types/tool'
import type { WikiVisibility } from '../types/wiki'

const API_BASE_URL = '/web/internal'

export class ApiError extends Error {
  status: number
  data: unknown
  constructor(message: string, status: number, data?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

type RequestOptions = { method?: string; body?: unknown; headers?: Record<string, string>; base?: string }

async function handleRequest(endpoint: string, options: RequestOptions = {}) {
  const { method = 'GET', body } = options
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-CSRF-Token': csrfToken(),
    Accept: 'application/json',
    ...options.headers,
  }

  // base: '' for endpoints outside /web/internal (the artifact bridge serves
  // people who are not logged in, spec §7).
  const response = await fetch(`${options.base ?? API_BASE_URL}${endpoint}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
    credentials: 'include',
  })

  let data: any
  const contentType = response.headers.get('content-type')
  if (contentType && contentType.includes('application/json')) {
    data = await response.json()
  } else {
    data = await response.text()
  }

  if (!response.ok) {
    throw new ApiError((data && data.error) || 'Unknown error occurred', response.status, data)
  }
  return data
}

async function postForm(endpoint: string, fields: Record<string, string | Blob>) {
  const form = new FormData()
  Object.entries(fields).forEach(([key, value]) => form.append(key, value))
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'POST',
    headers: { 'X-CSRF-Token': csrfToken(), Accept: 'application/json' },
    body: form,
    credentials: 'include',
  })
  const data = await response.json().catch(() => ({}))
  if (!response.ok) throw new ApiError(data?.error || t('上传失败', 'Upload failed'), response.status, data)
  return data
}

const api = {
  auth: {
    // Returns { id, email, name, daily_completed_pomodoros } for the current session user.
    me: () => handleRequest('/me'),
  },

  // Agent console (spec 2026-10-15 §7).
  agent: {
    bootstrap: () => handleRequest('/agent'),
    list: () => handleRequest('/agent/conversations'),
    show: (id: number) => handleRequest(`/agent/conversations/${id}`),
    create: (text: string, page: PageContext) =>
      handleRequest('/agent/conversations', { method: 'POST', body: { text, page } }),
    send: (id: number, text: string, page: PageContext) =>
      handleRequest(`/agent/conversations/${id}/messages`, { method: 'POST', body: { text, page } }),
    cancel: (id: number) => handleRequest(`/agent/conversations/${id}/cancel`, { method: 'POST' }),
    approve: (id: number, proposalId: string) =>
      handleRequest(`/agent/conversations/${id}/proposals/${proposalId}/approve`, { method: 'POST' }),
    decline: (id: number, proposalId: string, note?: string) =>
      handleRequest(`/agent/conversations/${id}/proposals/${proposalId}/decline`, { method: 'POST', body: { note } }),
    // The panel's 定时 list (docs/design/agent-scheduled-tasks.html §3.4).
    schedules: {
      list: () => handleRequest('/agent/schedules'),
      update: (id: number, changes: Record<string, unknown>) =>
        handleRequest(`/agent/schedules/${id}`, { method: 'PATCH', body: changes }),
      destroy: (id: number) => handleRequest(`/agent/schedules/${id}`, { method: 'DELETE' }),
      run: (id: number) => handleRequest(`/agent/schedules/${id}/run`, { method: 'POST' }),
    },
  },

  projects: {
    list: () => handleRequest('/projects'),
    create: (project: Record<string, unknown>) =>
      handleRequest('/projects', { method: 'POST', body: { project } }),
    update: (id: number, project: Record<string, unknown>) =>
      handleRequest(`/projects/${id}`, { method: 'PATCH', body: { project } }),
    destroy: (id: number) => handleRequest(`/projects/${id}`, { method: 'DELETE' }),
    archive: (id: number) => handleRequest(`/projects/${id}/archive`, { method: 'POST' }),
    unarchive: (id: number) => handleRequest(`/projects/${id}/unarchive`, { method: 'POST' }),
    reorder: (order: number[]) =>
      handleRequest('/projects/reorder', { method: 'POST', body: { order } }),
    // 共享名单（PRD 2026-10-27-collaboration-roles §10）。只有主人调得动这三条，
    // 服务端从 current_user.projects 起手，别人的项目一律 404。
    // 没有 list：名单跟着 /projects 那一页的 props 一起下发。
    share: (projectId: number, email: string, role: string) =>
      handleRequest(`/projects/${projectId}/memberships`, { method: 'POST', body: { email, role } }),
    setRole: (projectId: number, membershipId: number, role: string) =>
      handleRequest(`/projects/${projectId}/memberships/${membershipId}`, {
        method: 'PATCH',
        body: { role },
      }),
    unshare: (projectId: number, membershipId: number) =>
      handleRequest(`/projects/${projectId}/memberships/${membershipId}`, { method: 'DELETE' }),
  },

  // 共享输入框里「找人」。服务端按显示名搜全站，邮箱只认全等（见 People
  // 服务）。必须带着输入的内容去问，不能一次取回一份名册在前端筛。
  people: {
    search: (q: string) => handleRequest(`/people?q=${encodeURIComponent(q)}`),
  },

  plan: {
    fetch: (options: { groupBy?: string; from?: string; to?: string } = {}) => {
      const query = new URLSearchParams();
      if (options.groupBy) query.set('group_by', options.groupBy);
      if (options.from) query.set('from', options.from);
      if (options.to) query.set('to', options.to);
      const suffix = query.toString();
      return handleRequest(`/tasks/plan${suffix ? `?${suffix}` : ''}`);
    },
    backlog: () => handleRequest('/tasks/backlog'),
    updateTask: (id: number, task: Record<string, unknown>) =>
      handleRequest(`/tasks/${id}`, { method: 'PATCH', body: { task } }),
  },

  aiTime: {
    // 任务详情「时间记录」里算给这条任务的 AI 共创时间，按关联的 worklog 一条一行，只读。
    forTask: (taskId: number) => handleRequest(`/tasks/${taskId}/ai_time`),
  },
  timeEntries: {
    current: () => handleRequest('/time_entries/current'),
    start: (payload: Record<string, unknown>) =>
      handleRequest('/time_entries/start', { method: 'POST', body: payload }),
    stop: () => handleRequest('/time_entries/stop', { method: 'POST' }),
    list: (range: { from?: string; to?: string; taskId?: number } = {}) => {
      const query = new URLSearchParams();
      if (range.from) query.set('from', range.from);
      if (range.to) query.set('to', range.to);
      // /todo 详情栏按任务取「时间记录」，不塞进页面 props——否则每条任务都
      // 驮着自己的计时历史
      if (range.taskId) query.set('task_id', String(range.taskId));
      const suffix = query.toString();
      return handleRequest(`/time_entries${suffix ? `?${suffix}` : ''}`);
    },
    create: (timeEntry: Record<string, unknown>) =>
      handleRequest('/time_entries', { method: 'POST', body: { time_entry: timeEntry } }),
    update: (id: number, timeEntry: Record<string, unknown>) =>
      handleRequest(`/time_entries/${id}`, { method: 'PATCH', body: { time_entry: timeEntry } }),
    destroy: (id: number) => handleRequest(`/time_entries/${id}`, { method: 'DELETE' }),
  },

  worklogs: {
    // 列表走 Inertia props，这里只有「取正文」「改标签」和「删除」。
    get: (id: number) => handleRequest(`/worklogs/${id}`),
    // 整份替换，[] 清空。正文 append-only，这里只收 tags。
    retag: (id: number, tags: string[]) =>
      handleRequest(`/worklogs/${id}`, { method: 'PATCH', body: { tags } }),
    // AI 只出主意，不写库：{ tags, new_tags }，new_tags 是这个人没用过的那几个。
    suggestTags: (id: number) => handleRequest(`/worklogs/${id}/tag_suggestion`, { method: 'POST' }),
    // 给没标签的日志批量打上：{ tagged, considered, untagged_count }。
    autoTag: () => handleRequest('/worklog_auto_tag', { method: 'POST' }),
    destroy: (id: number) => handleRequest(`/worklogs/${id}`, { method: 'DELETE' }),
  },

  wikiPages: {
    // 建页只收标题：这一期没有「新建页面」那种带正文的表单，列表页那个输入框
    // 敲完标题就建，正文进编辑器再写。
    create: (title: string) =>
      handleRequest('/wiki_pages', { method: 'POST', body: { title } }),
    // base_version 是进编辑态时那一版的号。服务端不一致就回 409，
    // handleRequest 会抛 ApiError（status 409，data.current_version 是当前版本）。
    //
    // body 直接摊平，不包在 { wiki_page: ... } 里 —— 控制器读的是顶层 params，
    // 与 tasks 那组的 { task } 包法不同。
    //
    // base_version 是可选的：标签编辑那个入口不传它。乐观锁保护的是「正文被
    // 覆盖」，而改标签不覆盖正文 —— 为它回 409 只会让人一脸问号（服务端那侧
    // 「不传就不检查」是同一条规矩）。
    update: (
      urlKey: string,
      payload: {
        title?: string;
        body_md?: string;
        tags?: string[];
        base_version?: number;
        auto_saved?: boolean;
      },
    ) => handleRequest(`/wiki_pages/${urlKey}`, { method: 'PATCH', body: payload }),
    // 形状与 worklogs.destroy 逐字一致。助手叫 handleRequest（不是 request），
    // 它已经处理了 API_BASE_URL（'/web/internal'）、CSRF 头和错误抛 ApiError。
    //
    // destroy 回 204、没有响应体 —— handleRequest 对此天然安全：它按
    // content-type 分流，不是 JSON 就走 response.text()，所以不会在空体上抛
    // JSON 解析错误。不需要在这里做任何特殊处理。
    destroy: (urlKey: string) => handleRequest(`/wiki_pages/${urlKey}`, { method: 'DELETE' }),
    restore: (urlKey: string) => handleRequest(`/wiki_pages/${urlKey}/restore`, { method: 'POST' }),
    // 回滚不删历史 —— 服务端拿那一版的内容写一个新版（§6.4）。
    rollback: (urlKey: string, version: number) =>
      handleRequest(`/wiki_pages/${urlKey}/rollback`, { method: 'POST', body: { version } }),
    // 分享设置不走上面的 update：那条路每次写一版历史，分享不是正文。
    // 回的是 WikiSharing 那个形状。
    updateSharing: (urlKey: string, payload: { visibility?: WikiVisibility; clear_shares?: boolean }) =>
      handleRequest(`/wiki_pages/${urlKey}/sharing`, { method: 'PATCH', body: payload }),
    shares: {
      create: (urlKey: string, email: string) =>
        handleRequest(`/wiki_pages/${urlKey}/shares`, { method: 'POST', body: { email } }),
      destroy: (urlKey: string, id: number) =>
        handleRequest(`/wiki_pages/${urlKey}/shares/${id}`, { method: 'DELETE' }),
    },
  },

  // 对外发布的文章。只有 admin 能走 —— 服务端那侧 include AdminOnly，
  // 非 admin 一律 404（不是 403：403 等于确认这里有个后台）。
  //
  // 按 id 而不是 slug：slug 是可改的，而编辑器手里拿着的是一条确定的记录，
  // 改完 slug 之后它还得能指向同一篇。公开那一侧才用 slug。
  posts: {
    create: (payload: { title: string; slug: string }) =>
      handleRequest('/posts', { method: 'POST', body: payload }),
    // 发布与下架也走这里（status: 'published' / 'draft'）——
    // 它们是同一个字段的两个值，单独开端点等于把一个字段的写入散成三处。
    update: (
      id: number,
      payload: {
        title?: string;
        slug?: string;
        body_md?: string;
        excerpt?: string;
        status?: 'draft' | 'published';
        tags?: string[];
        auto_saved?: boolean;
      },
    ) => handleRequest(`/posts/${id}`, { method: 'PATCH', body: payload }),
  },

  // 文章正文里粘的图片。形状与 wikiImages 逐字一致 —— 两者的区别只有地址，
  // 校验、挂载、绝对 URL 拼装全在服务端的 ImageUploadable 里。
  postImages: {
    create: async (postId: number, file: File): Promise<{ url: string }> => {
      const form = new FormData();
      form.append('file', file);

      const response = await fetch(`${API_BASE_URL}/posts/${postId}/images`, {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrfToken(), Accept: 'application/json' },
        body: form,
        credentials: 'include',
      });

      const data = await response.json();
      if (!response.ok) {
        throw new ApiError(data?.error || t('图片传不上去', 'Could not upload the image'), response.status, data);
      }
      return data;
    },
  },

  // /todo 右栏的留言。列表不进 Inertia props —— 否则每条任务都驮着自己的
  // 全部留言正文（与 timeEntries 同一个理由）。
  virtualOffice: {
    snapshot: () => handleRequest(`/virtual_office/snapshot`),
    // the tour's refresh: who is in, never who (spec §68); open to guests
    people: () => handleRequest(`/virtual_office/people`, { base: '' }),
  },
  notifications: {
    list: () => handleRequest(`/notifications`),
    read: (id: number) => handleRequest(`/notifications/${id}/read`, { method: 'POST' }),
    readAll: () => handleRequest(`/notifications/read_all`, { method: 'POST' }),
  },

  comments: {
    list: (taskId: number) => handleRequest(`/tasks/${taskId}/comments`),
    create: (taskId: number, bodyMd: string) =>
      handleRequest(`/tasks/${taskId}/comments`, {
        method: 'POST',
        body: { comment: { body_md: bodyMd } },
      }),
    update: (id: number, bodyMd: string) =>
      handleRequest(`/comments/${id}`, {
        method: 'PATCH',
        body: { comment: { body_md: bodyMd } },
      }),
    destroy: (id: number) => handleRequest(`/comments/${id}`, { method: 'DELETE' }),
  },

  mentionables: {
    // 这条任务所属项目里能 @ 到的人。按任务问，不是全站搜用户 ——
    // 后者等于把留言框变成邮箱探测器（PRD 2026-10-31-mentions §3）。
    list: (taskId: number) => handleRequest(`/tasks/${taskId}/mentionables`),
  },

  aiPrompt: {
    // 参数名是 action_kind 不是 action —— params[:action] 被 Rails 占着，
    // 它永远是控制器动作名。传空串 = 让服务端挑默认动作。
    show: (taskId: number, action: string) =>
      handleRequest(`/tasks/${taskId}/ai_prompt?action_kind=${encodeURIComponent(action)}`),
    // 清单级。view 是 /todo/:view 里那个键；项目清单额外带 project_id。
    list: (view: string, projectId: number | null, action: string) =>
      handleRequest(
        `/list_ai_prompt?view=${encodeURIComponent(view)}` +
          `${projectId ? `&project_id=${projectId}` : ''}` +
          `&action_kind=${encodeURIComponent(action)}`
      ),
  },

  // 「串起来」服务端直接执行版。只有单条级任务有，清单级没有这条通道——
  // 见 config/routes.rb 里 worklog_link 那条注释。
  worklogLink: {
    create: (taskId: number) =>
      handleRequest(`/tasks/${taskId}/worklog_link`, { method: 'POST' }),
  },

  // 「交给 Piano」：在这个 project 绑的仓库里起一个无人值守的 session。
  //
  // 只传动作，不传浮层里那段正文 —— 送去 Piano 的提示词由服务端从任务记录
  // 重新组装（见 Piano::StartSession 头注释）。手改过的内容只跟着「复制」走，
  // 浮层会在正文被改过时明说这件事。
  pianoSession: {
    // prompt 只在人手改过正文时才传。不传 = 服务端照任务记录自己组装那一份；
    // 传了也只有正文算数，末尾护栏一律由服务端换成无人值守版。
    create: (taskId: number, action: string, prompt?: string) =>
      handleRequest(`/tasks/${taskId}/piano_session`, {
        method: 'POST',
        body: prompt === undefined ? { action_kind: action } : { action_kind: action, prompt },
      }),
  },

  // 一个 project 绑一次 Piano 仓库。先试后存：服务端拿这几个值去问一次
  // Piano，404 才落库。
  pianoBinding: {
    create: (projectId: number, values: PianoBindingInput) =>
      handleRequest(`/projects/${projectId}/piano_binding`, {
        method: 'POST',
        body: { binding: values },
      }),
  },

  // 「串起来」批量版的状态胶囊——作用对象是 current_user 本人，不挂在某条
  // 任务或某个清单下面（asaptodo#472）。
  worklogLinkRun: {
    show: () => handleRequest('/worklog_link_run'),
    create: () => handleRequest('/worklog_link_run', { method: 'POST' }),
  },

  // 图片上传走 multipart，不能用 handleRequest —— 那个函数会把 body 当 JSON
  // 序列化并写死 Content-Type。这里只手动补 CSRF 头，其余交给浏览器（它会
  // 自己带上 multipart 的 boundary）。
  taskImages: {
    create: async (taskId: number, file: File): Promise<{ url: string }> => {
      const form = new FormData();
      form.append('file', file);

      const response = await fetch(`${API_BASE_URL}/tasks/${taskId}/images`, {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrfToken(), Accept: 'application/json' },
        body: form,
        credentials: 'include',
      });

      const data = await response.json();
      if (!response.ok) {
        throw new ApiError(data?.error || t('图片传不上去', 'Could not upload the image'), response.status, data);
      }
      return data;
    },
  },

  // 与 taskImages.create 同构：走 multipart，所以不能用 handleRequest（那个函数
  // 会把 body 当 JSON 序列化并写死 Content-Type）。
  //
  // 这一处确实照 taskImages.create 写了一份，而那不违反 §8.1 的「零处复刻」：
  // 不能复制的是校验、image_url_for、以及 useMarkdownImages 里那两个不显然的
  // 地方 —— 这 12 行是 fetch 的样板，没有承载任何论证。
  wikiImages: {
    create: async (urlKey: string, file: File): Promise<{ url: string }> => {
      const form = new FormData();
      form.append('file', file);

      const response = await fetch(`${API_BASE_URL}/wiki_pages/${urlKey}/images`, {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrfToken(), Accept: 'application/json' },
        body: form,
        credentials: 'include',
      });

      const data = await response.json();
      if (!response.ok) {
        throw new ApiError(data?.error || t('图片传不上去', 'Could not upload the image'), response.status, data);
      }
      return data;
    },
  },

  // The bottom input bar (components/InputCenter). Read-only; writes go
  // through tasks / projects below.
  inbox: {
    context: () => handleRequest('/inbox/context'),
    similar: (q: string, opts: { openOnly?: boolean; min?: number; limit?: number } = {}) => {
      const params = new URLSearchParams({ q })
      if (opts.openOnly) params.set('open_only', '1')
      if (opts.min != null) params.set('min', String(opts.min))
      if (opts.limit != null) params.set('limit', String(opts.limit))
      return handleRequest(`/inbox/similar?${params}`)
    },
    tasks: (filter: { q?: string; view?: string; projectId?: number; tag?: string }) => {
      const params = new URLSearchParams()
      if (filter.q) params.set('q', filter.q)
      if (filter.view) params.set('view', filter.view)
      if (filter.projectId) params.set('project_id', String(filter.projectId))
      if (filter.tag) params.set('tag', filter.tag)
      return handleRequest(`/inbox/tasks?${params}`)
    },
    understand: (text: string) => handleRequest('/inbox/understand', { method: 'POST', body: { text } }),
    progress: (projectId: number) => handleRequest(`/inbox/progress?project_id=${projectId}`),
    overview: () => handleRequest('/inbox/overview'),
    // 「继续」 (input-center PRD §28): what was last in progress.
    resume: (projectId?: number) => handleRequest(`/inbox/resume${projectId ? `?project_id=${projectId}` : ''}`),
    // Multipart, like taskImages.create: handleRequest JSON-encodes the body.
    // An image is read together with the typed text (its instruction).
    understandImage: (text: string, image: File) => postForm('/inbox/understand', { text, image }),
    // Speech → text only; the text goes back into the box and is routed like
    // typing, so the person can still edit it before Enter.
    transcribe: (audio: Blob) => postForm('/inbox/transcribe', { audio: new File([audio], 'voice.webm', { type: audio.type || 'audio/webm' }) }),
    worklogs: (filter: { q?: string; since?: string; until?: string }) => {
      const params = new URLSearchParams()
      if (filter.q) params.set('q', filter.q)
      if (filter.since) params.set('since', filter.since)
      if (filter.until) params.set('until', filter.until)
      return handleRequest(`/inbox/worklogs?${params}`)
    },
  },

  settings: {
    // Returns camelCase settings ({ workDuration, breakDuration, ... }) as the
    // PomodoroContext expects.
    get: () => handleRequest('/settings'),
  },

  tasks: {
    getAll: () => handleRequest('/tasks'),
    getTodoList: () => handleRequest('/tasks/todo_list'),
    getCompletedToday: () => handleRequest('/tasks/completed_today'),
    getCategories: () => handleRequest('/tasks/categories'),
    get: (id: number) => handleRequest(`/tasks/${id}`),
    create: (task: Record<string, unknown>) => handleRequest('/tasks', { method: 'POST', body: { task } }),
    update: (id: number, task: Record<string, unknown>) => handleRequest(`/tasks/${id}`, { method: 'PUT', body: { task } }),
    updatePosition: (id: number, position: number) => handleRequest(`/tasks/${id}`, { method: 'PUT', body: { position } }),
    // delete 是软删（进回收站），restore 是它的反向操作。
    delete: (id: number) => handleRequest(`/tasks/${id}`, { method: 'DELETE' }),
    restore: (id: number) => handleRequest(`/tasks/${id}/restore`, { method: 'POST' }),
    // Archive keeps the task but takes it out of every list. restoreDate is
    // the undo: it puts the cleared plan date back (and can hit the daily cap).
    archive: (id: number) => handleRequest(`/tasks/${id}/archive`, { method: 'POST' }),
    unarchive: (id: number, opts: { restoreDate?: boolean } = {}) =>
      handleRequest(`/tasks/${id}/unarchive`, {
        method: 'POST',
        body: { restore_date: opts.restoreDate ?? false },
      }),
    reorder: (taskIds: number[]) => handleRequest('/tasks/reorder', { method: 'POST', body: { order: taskIds } }),
    // 「挂一条已有任务」的候选。只读；挂上/解绑走 update(parent_id)。
    linkable: (q: string, under: number) =>
      handleRequest(`/tasks/linkable?q=${encodeURIComponent(q)}&under=${under}`),
    // 这一条单独分享给了谁（docs/design/task-sharing-design.html）。只有能管
    // 名单的人调得动（项目主人，或不在项目里时建它的人），别人一律 404。
    shares: {
      list: (taskId: number) => handleRequest(`/tasks/${taskId}/shares`),
      create: (taskId: number, email: string, role: string) =>
        handleRequest(`/tasks/${taskId}/shares`, { method: 'POST', body: { email, role } }),
      setRole: (taskId: number, shareId: number, role: string) =>
        handleRequest(`/tasks/${taskId}/shares/${shareId}`, { method: 'PATCH', body: { role } }),
      destroy: (taskId: number, shareId: number) =>
        handleRequest(`/tasks/${taskId}/shares/${shareId}`, { method: 'DELETE' }),
    },
  },

  // Rename / merge / remove a tag across every task (spec §4.5). Names go in
  // the body, so no URL encoding.
  taskTags: {
    rename: (from: string, to: string): Promise<{ affected: number; merged: boolean }> =>
      handleRequest('/task_tags/rename', { method: 'POST', body: { from, to } }),
    remove: (name: string): Promise<{ affected: number }> =>
      handleRequest('/task_tags/remove', { method: 'POST', body: { name } }),
  },

  pomodoros: {
    getAll: () => handleRequest('/pomodoros'),
    get: (id: string) => handleRequest(`/pomodoros/${id}`),
    create: (pomodoro: Record<string, unknown>) => handleRequest('/pomodoros', { method: 'POST', body: { pomodoro } }),
    update: (id: string, pomodoro: Record<string, unknown>) => handleRequest(`/pomodoros/${id}`, { method: 'PUT', body: { pomodoro } }),
    delete: (id: string) => handleRequest(`/pomodoros/${id}`, { method: 'DELETE' }),
  },

  googleCalendar: {
    // 集成配没配 + 当前用户连没连。决定日历页显示「连接」按钮还是直接拉事件。
    status: () => handleRequest('/google_calendar/status'),
    // 时间窗内的事件。from / to 为 ISO8601 字符串，缺省由后端给宽窗。
    events: (range: { from?: string; to?: string } = {}) => {
      const query = new URLSearchParams()
      if (range.from) query.set('from', range.from)
      if (range.to) query.set('to', range.to)
      const suffix = query.toString()
      return handleRequest(`/google_calendar/events${suffix ? `?${suffix}` : ''}`)
    },
  },

  // The 「我的」 menu (tools spec 2026-10-18 §8). create is 「另存为 Recipe」;
  // data runs the artifact's recipe now, as me (§4.3).
  tools: {
    list: () => handleRequest('/tools'),
    create: (
      artifactUrlKey: string,
      tool: { name?: string; icon?: string; description?: string; open_mode?: 'page' | 'popup' }
    ) => handleRequest('/tools', { method: 'POST', body: { artifact_url_key: artifactUrlKey, tool } }),
    update: (toolKey: string, tool: { name?: string; icon?: string; pinned?: boolean; menu_position?: number }) =>
      handleRequest(`/tools/${toolKey}`, { method: 'PATCH', body: { tool } }),
    // deleteShared: also take back what I wrote where everyone could see it.
    destroy: (toolKey: string, deleteShared = false) =>
      handleRequest(`/tools/${toolKey}${deleteShared ? '?delete_shared=1' : ''}`, { method: 'DELETE' }),
    data: (toolKey: string) => handleRequest(`/tools/${toolKey}/data`),
    // Sharing and publishing (sharing PRD §9, Explore PRD 2026-10-19 §13):
    // install adds someone's shared tool to my menu; the rest are the
    // creator's, except takedowns, which are an admin's.
    install: (toolKey: string) => handleRequest(`/tools/${toolKey}/install`, { method: 'POST' }),
    // Agree to what a newer version reads on top of what I granted.
    grant: (toolKey: string) => handleRequest(`/tools/${toolKey}/grant`, { method: 'POST' }),
    stats: (toolKey: string) => handleRequest(`/tools/${toolKey}/stats`),
    visibility: (
      toolKey: string,
      body: { visibility: string; summary?: string; description?: string; confirmed?: boolean; c1_confirmed?: boolean },
    ) => handleRequest(`/tools/${toolKey}/visibility`, { method: 'PATCH', body }),
    publish: (toolKey: string, version: number, notes: string, sharing?: ToolSharingChoice) =>
      handleRequest(`/tools/${toolKey}/releases`, { method: 'POST', body: { version, notes, sharing } }),
    // 给自己用 / 大家一起用 (shared-data PRD 2026-10-29 §7).
    sharing: (toolKey: string, body: ToolSharingChoice) =>
      handleRequest(`/tools/${toolKey}/sharing`, { method: 'PATCH', body }),
    draft: (toolKey: string, kind: 'listing' | 'release', version?: number) =>
      handleRequest(`/tools/${toolKey}/listing_draft`, { method: 'POST', body: { kind, version } }),
    takedown: (toolKey: string, kind: 'delist' | 'takedown' | 'restore', reason: string) =>
      handleRequest(`/tools/${toolKey}/takedowns`, { method: 'POST', body: { kind, reason } }),
  },

  artifacts: {
    // Soft delete. Submissions stay; the trash page can restore it.
    destroy: (urlKey: string) => handleRequest(`/artifacts/${urlKey}`, { method: 'DELETE' }),
    // Sharing switches, the management table's inline switches and renaming
    // (hub spec R1 / R3 / R6). search_indexable can only be turned on while
    // public.
    update: (
      urlKey: string,
      payload: {
        submissions_open?: boolean;
        submission_mode?: 'append' | 'per_person' | 'per_person_daily';
        visibility?: 'private' | 'site' | 'public';
        on_hub?: boolean;
        search_indexable?: boolean;
        clear_shares?: boolean;
        title?: string;
        // Whole-array replace; [] clears them.
        tags?: string[];
      }
    ) => handleRequest(`/artifacts/${urlKey}`, { method: 'PATCH', body: payload }),
    // The save-as-tool dialog's preview: the recipe run now, as me (tools
    // spec 2026-10-18 §8.1).
    liveData: (urlKey: string) => handleRequest(`/artifacts/${urlKey}/live_data`),
    // This person's pin on the workbench (hub spec R2).
    pin: (urlKey: string) => handleRequest(`/artifacts/${urlKey}/pin`, { method: 'POST' }),
    unpin: (urlKey: string) => handleRequest(`/artifacts/${urlKey}/pin`, { method: 'DELETE' }),
    // One change to several of mine (hub spec R6, H10).
    bulk: (urlKeys: string[], operation: string) =>
      handleRequest('/artifacts/bulk', { method: 'POST', body: { url_keys: urlKeys, operation } }),
    // Back from the trash: private, off the hub, no list (H11).
    restore: (urlKey: string) => handleRequest(`/artifacts/${urlKey}/restore`, { method: 'POST' }),
    // The owner page's 串连 panel (hub spec R7). add and remove answer with
    // the artifact's links as they are now.
    links: {
      candidates: (urlKey: string, q: string) =>
        handleRequest(`/artifacts/${urlKey}/links/candidates?q=${encodeURIComponent(q)}`),
      add: (urlKey: string, payload: { task_ids?: number[]; wiki_url_keys?: string[]; worklog_identifiers?: string[] }) =>
        handleRequest(`/artifacts/${urlKey}/links`, { method: 'POST', body: payload }),
      remove: (urlKey: string, payload: { task_ids?: number[]; wiki_url_keys?: string[]; worklog_identifiers?: string[] }) =>
        handleRequest(`/artifacts/${urlKey}/links`, { method: 'DELETE', body: payload }),
    },
    // The task detail's 关联的 artifact block (hub spec R7).
    linkedToTask: (taskId: number) => handleRequest(`/tasks/${taskId}/linked_artifacts`),
    shares: {
      create: (urlKey: string, email: string) =>
        handleRequest(`/artifacts/${urlKey}/shares`, { method: 'POST', body: { email } }),
      destroy: (urlKey: string, id: number) =>
        handleRequest(`/artifacts/${urlKey}/shares/${id}`, { method: 'DELETE' }),
    },
    // The owner's data panel: every submission, with who sent it.
    // excludeOwner: "不含我自己"; samePersonAs: the rows of whoever sent that one.
    submissions: {
      list: (urlKey: string, options: { limit?: number; excludeOwner?: boolean; samePersonAs?: number } = {}) => {
        const query = new URLSearchParams();
        if (options.limit) query.set('limit', String(options.limit));
        if (options.excludeOwner) query.set('exclude_owner', '1');
        if (options.samePersonAs) query.set('same_person_as', String(options.samePersonAs));
        const suffix = query.toString();
        return handleRequest(`/artifacts/${urlKey}/submissions${suffix ? `?${suffix}` : ''}`);
      },
      // One line per person.
      people: (urlKey: string, options: { excludeOwner?: boolean } = {}) =>
        handleRequest(`/artifacts/${urlKey}/submissions/people${options.excludeOwner ? '?exclude_owner=1' : ''}`),
      destroy: (id: number) => handleRequest(`/artifact_submissions/${id}`, { method: 'DELETE' }),
    },
    // The bridge (spec §7): outside /web/internal. mine returns only what
    // the current person submitted (spec S4). key is checked on the server.
    // base is /artifacts/:url_key on an artifact page and /t/:tool_key in
    // a tool (shared-data PRD 2026-10-29 §12): the same calls, answered for
    // the artifact or for the tool.
    bridge: {
      submit: (base: string, payload: { data: unknown; version: number; key?: unknown; collection?: unknown }) =>
        handleRequest(`${base}/submissions`, { method: 'POST', body: payload, base: '' }),
      mine: (base: string, options: { limit?: number; key?: string; collection?: string; day?: string } = {}) => {
        const query = new URLSearchParams();
        if (options.limit) query.set('limit', String(options.limit));
        if (options.key !== undefined) query.set('key', options.key);
        if (options.collection !== undefined) query.set('collection', options.collection);
        if (options.day !== undefined) query.set('day', options.day);
        const suffix = query.toString();
        return handleRequest(`${base}/submissions/mine${suffix ? `?${suffix}` : ''}`, { base: '' });
      },
      me: (base: string) => handleRequest(`${base}/me`, { base: '' }),
      // version: the artifact page's version, whose declaration says which
      // collections are shared. A tool uses the version it serves.
      shared: {
        list: (base: string, collection: string, query: Record<string, string>) =>
          handleRequest(`${base}/shared/${encodeURIComponent(collection)}?${new URLSearchParams(query)}`, { base: '' }),
        stats: (base: string, collection: string, query: Record<string, string>) =>
          handleRequest(`${base}/shared/${encodeURIComponent(collection)}/stats?${new URLSearchParams(query)}`, { base: '' }),
        remove: (base: string, id: number, version: number) =>
          handleRequest(`${base}/shared/posts/${id}?version=${version}`, { method: 'DELETE', base: '' }),
        cursor: (base: string, version: number) => handleRequest(`${base}/shared?version=${version}`, { base: '' }),
      },
      // asaptodo.data() on an artifact page: the snapshot taken when this
      // version was written (tools spec 2026-10-18 §4.3).
      snapshot: (urlKey: string, version: number) =>
        handleRequest(`/artifacts/${urlKey}/data?v=${version}`, { base: '' }),
    },
  },
}

export default api
